import React, { useCallback, useState } from 'react'
import { Button, Input, InputNumber, Select, Space, Switch, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined, ApartmentOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { CnDatahubCategoryColumn, DatahubColumnType } from './datahub-interfaces'
import {
  DATAHUB_DATE_FORMAT,
  DATAHUB_FILTER_OP_OPTIONS,
  DatahubFilterCondition,
  DatahubFilterGroup,
  DatahubFilterLogicalOp,
  DatahubFilterOp,
  DatahubFilterValue,
  columnDataForDatahubCategory,
  createEmptyCondition,
  createEmptyFilterGroup,
  findColumnByDatakey,
  invalidFilterValues,
  isFilterGroup,
  opNeedsMultipleValues,
  opNeedsValue,
  opsForColumnType,
  parseSelectOptions,
  validateFilterValueForColumn,
} from './datahubFilter'

interface DatahubFilterBuilderProps {
  value: DatahubFilterGroup
  columns: CnDatahubCategoryColumn[]
  onChange: (group: DatahubFilterGroup) => void
}

/**
 * Cast the value for the type of the column
 * @param raw - The raw value
 * @param type - The type of the column
 * @returns The casted value 
 */
function castValueForType(
  raw: DatahubFilterValue | undefined,
  type: DatahubColumnType | undefined,
): DatahubFilterValue {
  if (raw === undefined || raw === null) {
    if (type === 'boolean') return false
    if (type === 'entier' || type === 'decimal') return 0
    return ''
  }
  if (type === 'boolean') return Boolean(raw)
  if (type === 'entier') {
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) ? Math.trunc(n) : 0
  }
  if (type === 'decimal') {
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) ? n : 0
  }
  return String(raw)
}

/**
 * Text input showing a format error. The error stays hidden while the input is focused
 * so that a value being typed is not flagged before it is complete.
 * @param value - The current value
 * @param placeholder - The placeholder of the input
 * @param error - The format error message (null when the value is valid)
 * @param onValueChange - The function to call when the value changes
 */
function ValidatedTextInput({
  value,
  placeholder,
  error,
  onValueChange,
}: {
  value: string
  placeholder: string
  error: string | null
  onValueChange: (value: string) => void
}) {
  const [focused, setFocused] = useState(false)
  const showError = Boolean(error) && !focused

  return (
    <Tooltip title={error ?? ''} open={showError ? undefined : false}>
      <Input
        size="small"
        style={{ minWidth: 140, flex: 1 }}
        placeholder={placeholder}
        status={showError ? 'error' : undefined}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onValueChange(e.target.value)}
      />
    </Tooltip>
  )
}

/**
 * Filter value input component (input for the value of the condition)
 * @param column - The column
 * @param op - The operator
 * @param value - The value
 * @param values - The values
 * @param onValueChange - The function to call when the value changes
 * @param onValuesChange - The function to call when the values change
 */
function FilterValueInput({
  column,
  op,
  value,
  values,
  onValueChange,
  onValuesChange,
}: {
  column: CnDatahubCategoryColumn | undefined
  op: DatahubFilterOp
  value: DatahubFilterValue | undefined
  values: DatahubFilterValue[] | undefined
  onValueChange: (value: DatahubFilterValue) => void
  onValuesChange: (values: DatahubFilterValue[]) => void
}) {
  if (!opNeedsValue(op)) return null

  const type = column?.type
  const selectOpts = parseSelectOptions(column?.options)
  const valueError = validateFilterValueForColumn(value, column, op)

  // Multiple values input (for the 'in' and 'not_in' operators)
  if (opNeedsMultipleValues(op)) {
    const invalidValues = invalidFilterValues(values, column, op).map(String)
    const valuesError =
      invalidValues.length > 0 ? `Format invalide : ${invalidValues.join(', ')}` : null

    // Select input => options allowed are the options of the column
    if ((type === 'select' || type === 'multiple-labels') && selectOpts.length > 0) {
      return (
        <Tooltip title={valuesError ?? ''} open={valuesError ? undefined : false}>
          <Select
            mode="multiple"
            size="small"
            style={{ minWidth: 160, flex: 1 }}
            placeholder="Valeurs"
            status={valuesError ? 'error' : undefined}
            value={(values ?? []).map(String)}
            options={selectOpts.map((opt) => ({ label: opt, value: opt }))}
            onChange={(selected) => onValuesChange(selected)}
          />
        </Tooltip>
      )
    }

    // Tags input => values are entered manually
    const invalidSet = new Set(invalidValues)
    return (
      <Tooltip title={valuesError ?? ''} open={valuesError ? undefined : false}>
        <Select
          mode="tags"
          size="small"
          style={{ minWidth: 160, flex: 1 }}
          placeholder="Valeurs (Entrée pour ajouter)"
          status={valuesError ? 'error' : undefined}
          value={(values ?? []).map(String)}
          tagRender={({ label, value: tag, closable, onClose }) => (
            <Tag
              color={invalidSet.has(String(tag)) ? 'error' : undefined}
              closable={closable}
              onClose={onClose}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              style={{ marginInlineEnd: 4 }}
            >
              {label}
            </Tag>
          )}
          onChange={(selected) => {
            if (type === 'entier' || type === 'decimal') {
              onValuesChange(
                selected.map((s) => {
                  const n = Number(s)
                  return Number.isFinite(n) ? (type === 'entier' ? Math.trunc(n) : n) : s
                }),
              )
            } else {
              onValuesChange(selected)
            }
          }}
          tokenSeparators={[',']}
        />
      </Tooltip>
    )
  }

  // Boolean input
  if (type === 'boolean') {
    return (
      <Switch
        size="small"
        checked={Boolean(value)}
        checkedChildren="vrai"
        unCheckedChildren="faux"
        onChange={(checked) => onValueChange(checked)}
        style={{ marginLeft: 5 }}
      />
    )
  }

  // Select input (except 'in' and 'not_in' operators) => options allowed are the options of the column
  if ((type === 'select' || type === 'multiple-labels') && selectOpts.length > 0) {
    return (
      <Tooltip title={valueError ?? ''} open={valueError ? undefined : false}>
        <Select
          size="small"
          style={{ minWidth: 140, flex: 1 }}
          placeholder="Valeur"
          status={valueError ? 'error' : undefined}
          value={value === undefined || value === '' ? undefined : String(value)}
          options={selectOpts.map((opt) => ({ label: opt, value: opt }))}
          onChange={(selected) => onValueChange(selected)}
          allowClear
        />
      </Tooltip>
    )
  }

  // Number input (for the 'entier' and 'decimal' column types)
  if (type === 'entier' || type === 'decimal') {
    return (
      <Tooltip title={valueError ?? ''} open={valueError ? undefined : false}>
        <InputNumber
          size="small"
          style={{ minWidth: 120, flex: 1 }}
          status={valueError ? 'error' : undefined}
          value={typeof value === 'number' ? value : value === '' || value === undefined ? null : Number(value)}
          step={type === 'decimal' ? 0.1 : 1}
          precision={type === 'entier' ? 0 : undefined}
          onChange={(n) => onValueChange(n ?? 0)}
        />
      </Tooltip>
    )
  }

  // Date input (for the 'date' column type)
  if (type === 'date') {
    return (
      <ValidatedTextInput
        placeholder={DATAHUB_DATE_FORMAT}
        error={valueError}
        value={value === undefined ? '' : String(value)}
        onValueChange={onValueChange}
      />
    )
  }

  // Text input (for the 'text' column type)
  return (
    <ValidatedTextInput
      placeholder="Valeur"
      error={valueError}
      value={value === undefined ? '' : String(value)}
      onValueChange={onValueChange}
    />
  )
}

/**
 * Condition row component
 * @param condition - The condition
 * @param columns - The columns
 * @param onChange - The function to call when the condition changes
 * @param onRemove - The function to call when the condition is removed
 */
function ConditionRow({
  condition,
  columns,
  onChange,
  onRemove,
}: {
  condition: DatahubFilterCondition
  columns: CnDatahubCategoryColumn[]
  onChange: (condition: DatahubFilterCondition) => void
  onRemove: () => void
}) {
  const fieldOptions = columnDataForDatahubCategory(columns)
  const column = findColumnByDatakey(columns, condition.field)
  const allowedOps = opsForColumnType(column?.type)
  const opOptions = DATAHUB_FILTER_OP_OPTIONS.filter((o) => allowedOps.includes(o.value))

  /**
   * Handle the change of the field of the condition (ex: 'surface' -> 'surface_m2')
   * @param field - The new field
   */
  const handleFieldChange = (field: string) => {
    const nextColumn = findColumnByDatakey(columns, field)
    const nextOps = opsForColumnType(nextColumn?.type)
    const op = nextOps.includes(condition.op) ? condition.op : nextOps[0] ?? 'eq'
    const next: DatahubFilterCondition = { field, op }
    if (opNeedsMultipleValues(op)) {
      next.values = []
    } else if (opNeedsValue(op)) {
      next.value = castValueForType(undefined, nextColumn?.type)
    }
    onChange(next)
  }

  /**
   * Handle the change of the operator of the condition (ex: '=' -> '>')
   * @param op - The new operator
   */
  const handleOpChange = (op: DatahubFilterOp) => {
    const next: DatahubFilterCondition = { field: condition.field, op }
    if (opNeedsMultipleValues(op)) {
      next.values = condition.values ?? (condition.value !== undefined ? [condition.value] : [])
    } else if (opNeedsValue(op)) {
      next.value = castValueForType(
        condition.value ?? condition.values?.[0],
        column?.type,
      )
    }
    onChange(next)
  }

  return (
    <Space.Compact style={{ width: '100%', display: 'flex' }}>
      <Select
        size="small"
        style={{ minWidth: 140, width: 160 }}
        placeholder="Colonne"
        value={condition.field || undefined}
        options={fieldOptions.map(({ label, value }) => ({ label, value }))}
        onChange={handleFieldChange}
        showSearch
        optionFilterProp="label"
      />
      <Select
        size="small"
        style={{ minWidth: 130, width: 150 }}
        value={condition.op}
        options={opOptions}
        onChange={handleOpChange}
        disabled={!condition.field}
      />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <FilterValueInput
          column={column}
          op={condition.op}
          value={condition.value}
          values={condition.values}
          onValueChange={(value) => onChange({ ...condition, value })}
          onValuesChange={(values) => onChange({ ...condition, values })}
        />
      </div>
      <Button
        size="small"
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      />
    </Space.Compact>
  )
}

/**
 * Filter group editor component
 * @param group - The group of the filter
 * @param columns - The columns
 * @param onChange - The function to call when the filter changes
 * @param onRemove - The function to call when the group is removed
 * @param depth - The depth of the group (0 for root, 1 for first level, etc.)
 */
function FilterGroupEditor({
  group,
  columns,
  onChange,
  onRemove,
  depth,
}: {
  group: DatahubFilterGroup
  columns: CnDatahubCategoryColumn[]
  onChange: (group: DatahubFilterGroup) => void
  onRemove?: () => void
  depth: number
}) {
  /**
   * Update the condition at the given index
   * @param index - The index of the condition
   * @param node - The new condition
   */
  const updateConditionAt = useCallback(
    (index: number, node: DatahubFilterCondition | DatahubFilterGroup) => {
      onChange({
        ...group,
        conditions: group.conditions.map((item, i) => (i === index ? node : item)),
      })
    },
    [group, onChange],
  )

  /**
   * Remove the condition at the given index
   * @param index - The index of the condition
   */
  const removeAt = useCallback(
    (index: number) => {
      onChange({
        ...group,
        conditions: group.conditions.filter((_, i) => i !== index),
      })
    },
    [group, onChange],
  )

  /**
   * Add a new condition to the group
   */
  const addCondition = () => {
    onChange({
      ...group,
      conditions: [...group.conditions, createEmptyCondition()],
    })
  }

  /**
   * Add a new group to the group
   */
  const addGroup = () => {
    onChange({
      ...group,
      conditions: [
        ...group.conditions,
        createEmptyFilterGroup(group.operator === 'and' ? 'or' : 'and'),
      ],
    })
  }

  /**
   * Set the operator of the group
   * @param operator - The new operator
   */
  const setOperator = (operator: DatahubFilterLogicalOp) => {
    onChange({ ...group, operator })
  }

  return (
    <div
      style={{
        border: depth === 0 ? undefined : '1px solid #f0f0f0',
        borderRadius: 6,
        padding: depth === 0 ? 0 : 8,
        background: depth % 2 === 1 ? 'rgb(224, 224, 224)' : undefined,
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {/* Operator selector */}
        <Space size="small">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Correspondre à
          </Typography.Text>
          <Select
            size="small"
            value={group.operator}
            style={{ width: 160 }}
            options={[
              { label: 'toutes (ET)', value: 'and' },
              { label: 'au moins une (OU)', value: 'or' },
            ]}
            onChange={setOperator}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            des conditions
          </Typography.Text>
        </Space>
        {/* Add condition button */}
        <Space size="small">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              addCondition()
            }}
          >
            Condition
            <Tooltip title="Ajoute une condition au groupe (ex: 'surface > 100')">
              <InfoCircleOutlined style={{ marginLeft: 6, color: 'rgba(0,0,0,0.45)' }} />
            </Tooltip>
          </Button>
          {/* Add group button (only at root level) */}
          {depth < 1 && (
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                addGroup()
              }}
            >
              Groupe
              <Tooltip title="Ajoute un groupe de conditions vide dans lequel vous pourrez ajouter une ou plusieurs conditions">
                <InfoCircleOutlined style={{ marginLeft: 6, color: 'rgba(0,0,0,0.45)' }} />
              </Tooltip>
            </Button>
          )}
          {/* Remove group button */}
          {onRemove && (
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
            />
          )}
        </Space>
      </div>

      {group.conditions.length === 0 ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Aucune condition — ajoutez au moins une condition.
        </Typography.Text>
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {group.conditions.map((node, index) =>
            isFilterGroup(node) ? (
              // Render a group editor (recursive)
              <FilterGroupEditor
                key={`filter-group-${index}`}
                group={node}
                columns={columns}
                depth={depth + 1}
                onChange={(updated) => updateConditionAt(index, updated)}
                onRemove={() => removeAt(index)}
              />
            ) : (
              // Render a condition row
              <ConditionRow
                key={`condition-${index}`}
                condition={node}
                columns={columns}
                onChange={(updated) => updateConditionAt(index, updated)}
                onRemove={() => removeAt(index)}
              />
            ),
          )}
        </Space>
      )}
    </div>
  )
}

/**
 * Datahub filter builder component
 * @param value - The value of the filter
 * @param columns - The columns (default columns of the datahub category)
 * @param onChange - The function to call when the filter changes
 */
export function DatahubFilterBuilder({ value, columns, onChange }: DatahubFilterBuilderProps) {
  const fields = columnDataForDatahubCategory(columns)

  if (fields.length === 0) {
    return (
      <Typography.Text type="warning" style={{ fontSize: 12 }}>
        Aucune colonne avec une clé source (datakey) — impossible de construire un filtre.
      </Typography.Text>
    )
  }

  return <FilterGroupEditor group={value} columns={columns} onChange={onChange} depth={0} />
}
